import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Grid,
  Typography,
  Breadcrumbs,
  Link,
  useTheme,
  useMediaQuery,
  Box,
  Switch,
} from "@mui/material";
import Layout from "../../../layout/Layout";
import { useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { config, hrMasterListAPI, baseURL } from "../../../config";
import AddIcon from "@mui/icons-material/Add";
import { MaterialReactTable } from "material-react-table";
import EditLocationOutlinedIcon from "@mui/icons-material/EditLocationOutlined";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useWindowSize } from "../../../hooks/useWindowSize";
import moment from "moment";

function ListEmployee() {
  const theme = useTheme();
  const lgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const navigate = useNavigate();
  const location = useLocation();

  const [rawEmployeeList, setRawEmployeeList] = useState([]);
  const [loading, setLoading] = useState(false);

  const isActiveEmployee = (employee) =>
    Number(employee.present_employee) === 2 &&
    employee.reason_for_relieve !== "Relieved" &&
    employee.reason_for_relieve !== "Terminated";

  const fetchUsers = async () => {
    setLoading(true);
    // Fetch all employees of the selected company (or all) to filter client-side
    const companyFilter = location.state?.companyID
      ? [{ id: "company_name", value: location.state.companyID }]
      : [];

    const data = {
      pageIndex: 0,
      pageSize: 10000,
      filters: companyFilter,
      sorting: [],
    };
    try {
      const response = await axios.post(hrMasterListAPI, data);
      setRawEmployeeList(response.data.result || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [location.state?.companyID]);

  const handleToggle2FA = async (employeeId, enabled) => {
    try {
      const data = JSON.stringify({ employeeId, enabled });
      config.method = "POST";
      config.data = data;
      config.url = `${baseURL}/login/toggle-2fa`;

      const response = await axios(config);
      if (response.data.success) {
        setRawEmployeeList((prev) =>
          prev.map((emp) =>
            emp.ID === employeeId
              ? { ...emp, google_authenticator_enabled: enabled ? 1 : 0 }
              : emp
          )
        );
      }
    } catch (error) {
      console.error("Error toggling 2FA:", error);
    }
  };

  // Client-side filtering based on incoming dashboard states
  const filteredEmployeeList = useMemo(() => {
    return rawEmployeeList.filter((emp) => {
      const state = location.state || {};

      // 1. Status Filter (Active / Inactive) from navigation state (if provided)
      if (state.status === "inactive") {
        const isInactive =
          Number(emp.present_employee) === 3 ||
          emp.reason_for_relieve === "Relieved" ||
          emp.reason_for_relieve === "Terminated";
        if (!isInactive) return false;
      } else if (state.status === "active") {
        if (!isActiveEmployee(emp)) return false;
      }

      // 2. Joined this Month Filter
      if (state.filterJoined) {
        if (!emp.date_of_join || emp.date_of_join === "0000-00-00") return false;
        const joinDate = moment(emp.date_of_join);
        const matchMonth =
          joinDate.month() + 1 === state.month &&
          joinDate.year() === state.year;
        if (!matchMonth) return false;
      }

      // 3. Relieved this Month Filter
      if (state.filterRelieved) {
        if (!emp.relieving_date || emp.relieving_date === "0000-00-00") return false;
        const relievedDate = moment(emp.relieving_date);
        const matchMonth =
          relievedDate.month() + 1 === state.month &&
          relievedDate.year() === state.year;
        if (!matchMonth) return false;
      }

      // 4. Incomplete Profile Filter
      if (state.incomplete) {
        const isIncomplete =
          !emp.phone || !emp.email || !emp.bank_name || emp.photo === "";
        if (!isIncomplete) return false;
      }

      // 5. Specific Employee IDs Filter (e.g. Never Logged In)
      if (state.employeeIds) {
        if (!state.employeeIds.includes(emp.ID)) return false;
      }
      if (state.employeeNames) {
        const lowerNames = state.employeeNames.map(n => n.toLowerCase().trim());
        if (!lowerNames.includes(emp.employee_name.toLowerCase().trim())) return false;
      }

      return true;
    });
  }, [rawEmployeeList, location.state]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "employee_no",
        header: "Emp. No",
        enableSorting: true,
        enableFiltering: true,
        size: 100,
      },
      {
        accessorKey: "photo",
        header: "Profile Image",
        enableSorting: false,
        enableColumnFilter: false,
        size: 80,
        muiTableBodyCellProps: {
          sx: {
            textAlign: "center",
            padding: "3px",
            border: "1px solid #ccc",
          },
        },
        Cell: ({ cell }) => {
          const photoUrl = cell.getValue();
          return (
            <Box
              component="img"
              src={photoUrl || "https://controller.kgbuildtechapp.com/profile/employee.png"}
              sx={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }}
            />
          );
        },
      },
      {
        accessorKey: "employee_name",
        header: "Name",
        enableSorting: true,
        size: 150,
      },
      {
        accessorKey: "phone",
        header: "Contact No",
        enableSorting: false,
        size: 120,
      },
      {
        accessorKey: "pin",
        header: "Login PIN",
        enableSorting: false,
        size: 90,
      },
      {
        accessorKey: "position_name",
        header: "Position",
        enableSorting: false,
        size: 120,
      },
      {
        accessorKey: "company_name",
        header: "Company",
        enableSorting: false,
        size: 130,
      },
      {
        accessorKey: "present_employee",
        header: "Status",
        Cell: ({ cell }) => {
          const status = Number(cell.getValue());
          const statusText = status === 3 ? "Inactive" : "Active";

          return (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  width: "7rem",
                  textAlign: "center",
                  backgroundColor: status === 3 ? "#fae0e4" : "#d8f3dc",
                  color: status === 3 ? "#ff0a54" : "#006400",
                  fontWeight: "bold",
                  padding: "2px 1px",
                  borderRadius: "5px",
                }}
              >
                {statusText}
              </Typography>
            </div>
          );
        },
        filterVariant: "select",
        filterSelectOptions: [
          {
            label: "Active",
            title: "Active",
            value: 2,
          },
          {
            label: "Inactive",
            title: "Inactive",
            value: 3,
          },
        ],
        size: 100,
      },
      {
        accessorKey: "reason_for_relieve",
        header: "Reason",
        Cell: ({ cell }) => (
          <Typography
            style={{
              fontSize: "14px",
              width: "7rem",
              textAlign: "center",
              color: cell.getValue() ? "#ff0a54" : "#006400",
              fontWeight: "bold",
            }}
          >
            {cell.getValue() || "Present"}
          </Typography>
        ),
        filterVariant: "select",
        filterSelectOptions: [
          {
            label: "Active",
            title: "Active",
            value: "Active",
          },
          {
            label: "Relieved",
            title: "Relieved",
            value: "Relieved",
          },
          {
            label: "Terminated",
            title: "Terminated",
            value: "Terminated",
          },
        ],
        size: 100,
      },
      {
        accessorKey: "google_authenticator_enabled",
        header: "Authenticator",
        enableSorting: true,
        enableColumnFilter: false,
        size: 120,
        Cell: ({ row }) => (
          <Switch
            checked={row.original.google_authenticator_enabled === 1}
            onChange={(e) => handleToggle2FA(row.original.ID, e.target.checked)}
            color="primary"
          />
        ),
      },
      {
        accessorKey: "ID",
        header: "Action",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <IconButton
            color="warning"
            onClick={() => navigate(`/employee/view/${row.original.ID}`)}
          >
            <EditLocationOutlinedIcon />
          </IconButton>
        ),
        size: 50,
      },
    ],
    [navigate, rawEmployeeList]
  );

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} lg={6} md={6}>
            <Typography className="page_title">List Employee</Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">List Employee</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid item xs={12} lg={6} md={6} textAlign="right">
            <LoadingButton
              size="medium"
              variant="contained"
              color="success"
              href="/employee/add"
              startIcon={<AddIcon />}
              sx={{ mr: 2 }}
            >
              Add Profile
            </LoadingButton>
          </Grid>
        </Grid>

        <MaterialReactTable
          columns={columns}
          data={filteredEmployeeList}
          enableGlobalFilter={false}
          enableFullScreenToggle={false}
          enableDensityToggle={false}
          className="mat-react-tbl"
          state={{
            isLoading: loading,
          }}
          initialState={{
            columnPinning: lgUp ? { right: ["present_employee", "ID"] } : {},
          }}
          muiTableBodyCellProps={{
            sx: {
              padding: "3px 8px",
              border: "1px solid #ccc",
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              "& input": {
                color: "#fff !important",
                "&::placeholder": {
                  color: "#fff !important",
                  opacity: 1,
                },
              },
              "& .MuiSelect-select": {
                color: "#fff !important",
              },
              "& .MuiSelect-icon": {
                color: "#fff !important",
              },
              color: "#fff",
              fontWeight: "bold",
              "& svg": {
                color: "#fff",
              },
              "& .MuiTableSortLabel-root": {
                color: "#495057",
                "&.Mui-active": {
                  color: "#fff",
                  "& .MuiTableSortLabel-icon": {
                    color: "#fff !important",
                  },
                },
              },
              "& .MuiTableSortLabel-icon": {
                color: "#fff !important",
              },
            },
          }}
          muiTableBodyRowProps={({ row }) => ({
            sx: {
              borderBottom: "1px solid #ccc",
              backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#e3f2fd",
            },
          })}
          muiTableHeadCellFilterTextFieldProps={{
            sx: {
              "& input": {
                color: "#fff !important",
              },
              "& .MuiInput-underline:before": {
                borderBottomColor: "#fff !important",
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottomColor: "#fff !important",
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: "#fff !important",
              },
            },
          }}
        />
      </Grid>
    </Layout>
  );
}

export default ListEmployee;
